import { ReactNode, useEffect, useState } from 'react';
import AutoComplete, { AutoCompleteInputVariant, IAutoCompleteProps } from '.';
import { preventTouchMove } from '@/shared/helpers/scroll';
import { setIsPopoverAutocompleteOpen } from '@/store/slices/onboarding';
import { useDispatch } from 'react-redux';
import { createPortal } from 'react-dom';

type IProps<TOption> = IAutoCompleteProps<TOption> & {
    searched?: boolean;
    initialIntent: string;
};

export function PopoverAutoComplete<TOption>({
    value,
    onChange,
    onSelect,
    inputVariant,
    ...rest
}: IProps<TOption>) {
    const dispatch = useDispatch();
    const [inputValue, setInputValue] = useState(value || '');

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        const revertTouchMove = preventTouchMove();

        dispatch(setIsPopoverAutocompleteOpen(true));

        return () => {
            revertTouchMove();
            dispatch(setIsPopoverAutocompleteOpen(false));
            document.body.style.overflow = 'scroll';
        };
    }, []);

    return createPortal(
        <div className="fixed left-0 top-0 z-[1000] h-full w-full bg-[rgba(0,0,0,0.5)] pt-12">
            <div className="h-full overflow-auto rounded-t-20 bg-white px-2.5 pt-5">
                <Wrapper variant={inputVariant}>
                    <AutoComplete
                        {...rest}
                        popover
                        value={inputValue}
                        onChange={setInputValue}
                        onSelect={(value, label) => {
                            onChange?.(label);
                            onSelect(value, label);
                        }}
                        inputVariant={inputVariant}
                        onVoiceResult={setInputValue}
                    />
                </Wrapper>
            </div>
        </div>
    , document.getElementById('root')!);
}

const Wrapper = ({ variant = 'main', children }: { variant?: AutoCompleteInputVariant; children: ReactNode }) => {
    return variant === 'advanced' ? (
        <div className="fixed left-0 top-0 h-full w-full bg-[rgba(0,0,0,0.5)] pt-12">
            <div className="h-full overflow-auto rounded-t-20 bg-white pt-5">{children}</div>
        </div>
    ) : (
        <div className="fixed left-0 top-0 h-full w-full overflow-auto bg-white">{children}</div>
    );
};
